import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../Tools/Topbar'; // Component for the top navigation bar
import LeftSide from '../Tools/LeftSidebar'; // Component for the left sidebar
import EmployeeTab from '../tabs/EmployeeTab'; // Tab component for employee management
import AttendanceTab from '../tabs/AttendanceTab'; // Tab component for attendance tracking
import VehiclesTab from '../tabs/VehiclesTab'; // Tab component for vehicle management
import EquipmentTab from '../tabs/EquipmentTab'; // Tab component for equipment management
import MedicalTab from '../tabs/MedicalTab'; // Tab component for medical center management
import LocationTab from '../tabs/Locations'; // Tab component for location management
import AreaTab from '../tabs/Area'; // Tab component for area management
import VehicleChecklist from '../tabs/VehicleChecklistTab'; // Tab component for vehicle checklist
import Settings from '../Tools/Setting/Settings'; // Component for settings panel
import useSettings from '../Tools/Setting/useSettings'; // Custom hook for managing settings

// ResourceManagement component for managing various EMS resources through tabs
const ResourceManagement = () => {
  // State to manage the active tab
  const [activeTab, setActiveTab] = useState('employee');
  // State to toggle vehicle checklist view
  const [showVehicleChecklist, setShowVehicleChecklist] = useState(false);
  // State to manage sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // State to store user role name
  const [roleName, setRoleName] = useState('');

  // Custom hook for settings management
  const { updateSetting, handleColorTheme, settings } = useSettings();
  // Reference to Settings component
  const settingsRef = useRef();

  // Fetch role name from sessionStorage on component mount
  useEffect(() => {
    // Assuming Role_Name is stored in sessionStorage/localStorage after login
    const storedRole = sessionStorage.getItem('Role_Name') || '';
    setRoleName(storedRole);
  }, []); // Empty dependency array ensures this runs only once on mount

  // Function to toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle tab change and reset vehicle checklist if not on vehicles tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'vehicles') {
      setShowVehicleChecklist(false); // Hide vehicle checklist for non-vehicles tabs
    }
  };

  // Toggle between vehicle listing and vehicle checklist view
  const toggleVehicleChecklist = () => {
    setShowVehicleChecklist(!showVehicleChecklist);
  };

  // Get display label for each tab
  const getTabLabel = (tab) => ({
    'employee': 'Employee',
    'attendance': 'Attendance',
    'vehicles': 'Vehicles',
    'equipment': 'Equipment',
    'locations': 'Location',
    'area': 'Area',
    'medical': 'Medical Centers',
    'vehicleChecklist': 'Vehicle Checklist'
  }[tab] || tab);

  // Get icon class for each tab
  const getTabIcon = (tab) => ({
    'employee': 'users',
    'attendance': 'list',
    'vehicles': 'ti ti-ambulance',
    'equipment': 'asset',
    'locations': 'map-pin',
    'area': 'a-b-2',
    'medical': 'ti ti-nurse',
    'vehicleChecklist': 'file-check'
  }[tab] || 'circle');

  // Define all possible tabs
  const allTabs = ['employee', 'attendance', 'vehicles', 'equipment', 'locations', 'area', 'medical'];

  // Conditional tab filtering based on user role & medical center
  const filteredTabs = () => {
    const medicalCenterName = sessionStorage.getItem('Medical_Center_Name') || '';

    if (roleName === 'EMS Team') {
      return ['equipment', 'vehicles']; // Show only Equipment and Vehicles tabs for EMS Team
    }

    if (roleName === 'EMS Resource Manager' && medicalCenterName === '') {
      return ['employee', 'attendance', 'vehicles', 'equipment','medical'];; // Show all tabs if no medical center is assigned
    }

    if (roleName === 'EMS Resource Manager') {
      return ['employee', 'attendance', 'vehicles', 'equipment']; // Limited tabs
    }

    return allTabs; // All tabs for other roles
  };

  // Set default active tab based on available tabs
  useEffect(() => {
    const availableTabs = filteredTabs();
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] || 'employee'); // Set first available tab as default
    }
  }, [roleName]); // Re-run when roleName changes

  // Render the component
  return (
    <div id="main-wrapper">
      {/* Sidebar Component */}
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        {/* Topbar Component */}
        <TopBar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            {/* Settings Component */}
            <Settings 
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3 border-bottom">
                <h5 className="card-title fw-semibold mb-0 lh-sm">Resource Management</h5>
                <p className="card-subtitle mb-0 mt-1">Track, update, and organize resources</p>
              </div>

              <div className="card-body p-4">
                {/* Tab Navigation */}
                <ul className="nav nav-tabs border-bottom border-primary" role="tablist">
                  {filteredTabs().map((tab) => (
                    <li key={tab} className="nav-item" role="presentation">
                      <button
                        className={`nav-link d-flex ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab)}
                        aria-selected={activeTab === tab}
                        aria-controls={tab}
                      >
                        <span>
                          <i className={`ti ti-${getTabIcon(tab)} fs-5`}></i>
                        </span>
                        <span className="d-none d-md-block ms-2">{getTabLabel(tab)}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Tab Content */}
                <div className="tab-content pt-3">
                  {activeTab === 'employee' && filteredTabs().includes('employee') && <EmployeeTab />}
                  {activeTab === 'attendance' && filteredTabs().includes('attendance') && <AttendanceTab />}
                  {activeTab === 'vehicles' && filteredTabs().includes('vehicles') && (
                    <>
                      {!showVehicleChecklist && (
                        <>
                          <div className="d-flex justify-content-end mb-3">
                             {roleName !== "EMS Team" && (
                            <button 
                              className="btn btn-primary"
                              onClick={toggleVehicleChecklist}
                            >
                              <i className="ti ti-file-check me-2"></i>
                              Add Vehicle Checklist
                            </button>
                             )}
                          </div>
                          <VehiclesTab />
                        </>
                      )}
                      {showVehicleChecklist && (
                        <>
                          <div className="d-flex justify-content-end mb-3">
                            <button 
                              className="btn btn-primary"
                              onClick={toggleVehicleChecklist}
                            >
                              <i className="ti ti-arrow-back me-2"></i>
                              Back to Vehicle listing
                            </button>
                          </div>
                          <VehicleChecklist />
                        </>
                      )}
                    </>
                  )}
                  {activeTab === 'equipment' && filteredTabs().includes('equipment') && <EquipmentTab />}
                  {activeTab =='locations' && filteredTabs().includes('locations') && <LocationTab />}
                  {activeTab === 'area' && filteredTabs().includes('area') && <AreaTab />}
                  {activeTab === 'medical' && filteredTabs().includes('medical') && <MedicalTab />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );
};

// Export the component
export default ResourceManagement;
